/**
 * `useCursorFeed` — the SPA's ONE implementation of a keyset-paged list.
 *
 * A feed is a FIRST page, the batches scrolled in BELOW it (the tail), and
 * the cursor the next batch starts from. Three surfaces read one: the `/data`
 * browser (`pages/Data.tsx`), the Home audit card (`useAuditFeed`) and the
 * `/agents` activity panels (`useAgentActivity`). Until 2026-09-08 each
 * carried its own hand copy of the same discipline, and the `/data` copy had
 * drifted into a defect the other two never had: it armed its cursor from
 * WHATEVER response was on screen when the query changed, so typing a search
 * on a tall screen appended the old query's next batch — under the new
 * query's params — before the search's own first page had landed, and the one
 * member matching the search rendered twice (`2 rows — end of table` for one
 * row). The Home feed had solved exactly this on 2026-08-11 with a generation
 * counter; `Data.tsx` had patched around it twice on 2026-07-29 with refs
 * synchronised from the response object. This hook is the generation model,
 * shared, so the discipline cannot drift a third time.
 *
 * The invariant, stated once: **paging state belongs to exactly one query.**
 *
 *  - A change of `identity` bumps the generation and clears the tail and the
 *    cursor SYNCHRONOUSLY. Every page carries the generation it was requested
 *    under; a page landing for an older generation is dropped on arrival, so
 *    a stale request can never install rows or a cursor. The previous first
 *    page stays on screen until the new one lands (stale-while-revalidate,
 *    told apart by `firstIsLive`) — it just cannot page.
 *  - Only the first LIVE page of a generation arms the cursor. A `seed` (a
 *    client-cached page painted instantly for a new identity) never does: a
 *    cached `next_cursor` was the 2026-07-29 regression, twice over.
 *  - `refresh()` replaces the first page IN PLACE and re-arms only while the
 *    tail is empty. Once batches have been scrolled in, the cursor belongs to
 *    the last of them and a refreshed first page cannot move it (the 30 s
 *    safety poll used to snap the browser back to 25 rows).
 *  - Rows are de-duplicated on `rowKey` at the ONE place the first page and
 *    the tail are joined, first occurrence winning — so a refreshed first
 *    page that overlaps the tail (a row deleted above it, an audit row
 *    UPDATEd in place from `pending` to `ok`) shows once, with the fresh
 *    copy.
 *  - A batch whose `next_cursor` fails to advance ends the feed; so does a
 *    failed batch. The scroll sentinel therefore cannot spin.
 *
 * The state machine is a set of PURE transitions over `CursorFeedState`
 * (exported, pinned by `use-cursor-feed.test.ts` with no DOM); the hook is a
 * thin shell that owns the generation counter and the fetch plumbing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** The wire shape every paged endpoint shares: rows plus an opaque cursor. */
export interface CursorPage {
  readonly rows: ReadonlyArray<unknown>;
  readonly next_cursor: string | null;
}

export type RowOf<P extends CursorPage> = P["rows"][number];

export interface FirstPage<P extends CursorPage> {
  /** Generation the page was requested under (or seeded for). */
  readonly gen: number;
  readonly page: P;
  /** False for a `seed` — painted for speed, never trusted for paging. */
  readonly live: boolean;
}

export interface CursorFeedState<P extends CursorPage> {
  /** Current generation — bumped by every `resetFeed`. */
  readonly gen: number;
  /**
   * The first page on screen. May be a seed, or belong to an OLDER generation
   * (stale-while-revalidate) — neither arms the cursor.
   */
  readonly first: FirstPage<P> | null;
  /** Batches scrolled in under the current generation, in order. */
  readonly tail: ReadonlyArray<P>;
  /**
   * Where the next batch starts. `null` = nothing to fetch: the end of the
   * list, a stopped feed, or no live first page for this generation yet.
   */
  readonly cursor: string | null;
  /** The generation's first page (the reset fetch) is in flight. */
  readonly loadingFirst: boolean;
  /** A tail batch is in flight. */
  readonly loadingMore: boolean;
  readonly error: string | null;
}

const EMPTY: ReadonlyArray<never> = Object.freeze([]);

export function initialFeed<P extends CursorPage>(
  seed: P | null,
): CursorFeedState<P> {
  return {
    gen: 0,
    first: seed === null ? null : { gen: 0, page: seed, live: false },
    tail: EMPTY,
    cursor: null,
    loadingFirst: false,
    loadingMore: false,
    error: null,
  };
}

/** The live first page of the CURRENT generation is on screen. */
export function firstIsLive<P extends CursorPage>(
  s: CursorFeedState<P>,
): boolean {
  return s.first !== null && s.first.gen === s.gen && s.first.live;
}

/**
 * The query changed: claim a new generation, drop the tail and the cursor.
 * A `seed` paints at once (unarmed); without one the previous first page
 * stays on screen until the new one lands.
 */
export function resetFeed<P extends CursorPage>(
  s: CursorFeedState<P>,
  gen: number,
  seed: P | null,
): CursorFeedState<P> {
  return {
    gen,
    first: seed === null ? s.first : { gen, page: seed, live: false },
    tail: EMPTY,
    cursor: null,
    loadingFirst: true,
    loadingMore: false,
    error: null,
  };
}

/**
 * A first page landed (the reset fetch or a refresh). Dropped unless it was
 * requested under the current generation. Re-arms the cursor only while the
 * tail is empty. With `isSamePage`, a page identical to the live one on
 * screen is kept by IDENTITY so nothing re-renders on a quiet refresh.
 */
export function landFirst<P extends CursorPage>(
  s: CursorFeedState<P>,
  gen: number,
  page: P,
  isSamePage?: (prev: P, next: P) => boolean,
): CursorFeedState<P> {
  if (gen !== s.gen) return s;
  if (
    isSamePage !== undefined &&
    firstIsLive(s) &&
    s.first !== null &&
    isSamePage(s.first.page, page)
  ) {
    return s.loadingFirst || s.error !== null
      ? { ...s, loadingFirst: false, error: null }
      : s;
  }
  return {
    ...s,
    first: { gen, page, live: true },
    cursor: s.tail.length === 0 ? page.next_cursor : s.cursor,
    loadingFirst: false,
    error: null,
  };
}

/** A first-page fetch failed. The feed keeps what it shows; the cursor is untouched. */
export function failFirst<P extends CursorPage>(
  s: CursorFeedState<P>,
  gen: number,
  message: string,
): CursorFeedState<P> {
  if (gen !== s.gen) return s;
  return { ...s, loadingFirst: false, error: message };
}

export function startMore<P extends CursorPage>(
  s: CursorFeedState<P>,
): CursorFeedState<P> {
  return s.loadingMore ? s : { ...s, loadingMore: true };
}

/**
 * A tail batch landed for cursor `from`. Dropped unless requested under the
 * current generation. A cursor that did not advance ends the feed — the
 * sentinel would otherwise re-fire against it forever.
 */
export function landMore<P extends CursorPage>(
  s: CursorFeedState<P>,
  gen: number,
  from: string,
  page: P,
): CursorFeedState<P> {
  if (gen !== s.gen) return s;
  return {
    ...s,
    tail: [...s.tail, page],
    cursor: page.next_cursor === from ? null : page.next_cursor,
    loadingMore: false,
  };
}

/** A tail batch failed: the feed stops (a refresh with an empty tail re-arms it). */
export function failMore<P extends CursorPage>(
  s: CursorFeedState<P>,
  gen: number,
  message: string,
): CursorFeedState<P> {
  if (gen !== s.gen) return s;
  return { ...s, cursor: null, loadingMore: false, error: message };
}

/**
 * Drop everything scrolled in and page again from the first page. For
 * mutations that change WHICH rows exist (a delete) — the caller refreshes
 * afterwards, and the fresh first page re-arms the cursor because the tail
 * is now empty.
 */
export function dropTail<P extends CursorPage>(
  s: CursorFeedState<P>,
): CursorFeedState<P> {
  if (s.tail.length === 0) return s;
  return {
    ...s,
    tail: EMPTY,
    cursor: firstIsLive(s) && s.first !== null ? s.first.page.next_cursor : null,
  };
}

/**
 * Stop showing a first page from an OLDER generation. A seed or live page of
 * the current generation stays. For a consumer whose columns change with the
 * query (the `/data` browser on an entity switch) — a stale page of another
 * table must not paint under the new title.
 */
export function discardStale<P extends CursorPage>(
  s: CursorFeedState<P>,
): CursorFeedState<P> {
  if (s.first === null || s.first.gen === s.gen) return s;
  return { ...s, first: null };
}

function rowsOf<P extends CursorPage>(page: P): ReadonlyArray<RowOf<P>> {
  return page.rows as ReadonlyArray<RowOf<P>>;
}

/**
 * The list as rendered: the first page's rows, then every tail row not
 * already shown. De-duplication happens HERE and nowhere else — the first
 * occurrence wins, so a refreshed first page supplies the fresh copy of any
 * row the tail also holds. Rows without a key are never de-duplicated.
 * `rows` is the first page's own array by identity while the tail is empty.
 */
export function feedRows<P extends CursorPage>(
  s: CursorFeedState<P>,
  rowKey: (row: RowOf<P>) => string | undefined,
): {
  readonly rows: ReadonlyArray<RowOf<P>>;
  readonly tailRows: ReadonlyArray<RowOf<P>>;
} {
  const firstRows: ReadonlyArray<RowOf<P>> =
    s.first === null ? EMPTY : rowsOf(s.first.page);
  if (s.tail.length === 0) return { rows: firstRows, tailRows: EMPTY };
  const seen = new Set<string>();
  for (const row of firstRows) {
    const key = rowKey(row);
    if (key !== undefined) seen.add(key);
  }
  const tailRows: RowOf<P>[] = [];
  for (const page of s.tail) {
    for (const row of rowsOf(page)) {
      const key = rowKey(row);
      if (key !== undefined) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      tailRows.push(row);
    }
  }
  return {
    rows: tailRows.length === 0 ? firstRows : [...firstRows, ...tailRows],
    tailRows,
  };
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export interface CursorFeedOptions<P extends CursorPage> {
  /**
   * Identity of the QUERY — everything that changes which rows the server
   * returns, and nothing that merely re-fetches the same rows. A change
   * resets the feed under a new generation.
   */
  readonly identity: string;
  /**
   * Fetch one page; `null` = the first page. Read fresh on every call, so it
   * may close over the consumer's current filters.
   */
  readonly fetchPage: (cursor: string | null) => Promise<P>;
  /** Stable key per row for de-duplication; `undefined` = never de-duplicate this row. */
  readonly rowKey: (row: RowOf<P>) => string | undefined;
  /**
   * Instant paint for a new identity from a client-side cache. Rendered at
   * once, never trusted for paging — the live first page replaces it.
   */
  readonly seed?: (identity: string) => P | null;
  /** When false the feed neither resets, fetches nor refreshes (default true). */
  readonly enabled?: boolean;
  /** A landed first page identical to the live one keeps it by identity. */
  readonly isSamePage?: (prev: P, next: P) => boolean;
  /** Called for every first page that lands for the current generation. */
  readonly onFirstPage?: (page: P) => void;
  /**
   * Whether a failed `refresh()` is recorded as the feed's `error` (the
   * `/data` browser shows it) or swallowed so the view keeps what it has
   * until the next refresh succeeds (the Home poll). Default: swallowed.
   */
  readonly surfaceRefreshFailures?: boolean;
}

export interface CursorFeed<P extends CursorPage> {
  /** The first page on screen — a seed, the live page, or the previous query's page while the new one loads. */
  readonly first: P | null;
  /** `first` is the live page of the current query. */
  readonly firstIsLive: boolean;
  readonly loadingFirst: boolean;
  /** First page + tail, de-duplicated. */
  readonly rows: ReadonlyArray<RowOf<P>>;
  /** The tail alone, de-duplicated against the first page — for a table that renders the two separately. */
  readonly tailRows: ReadonlyArray<RowOf<P>>;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly error: string | null;
  readonly loadMore: () => void;
  /**
   * Re-read the first page in place. No-op while the generation's first page
   * is still in flight. Settles when the page has landed (or failed) — never
   * rejects — so a poll can avoid stacking ticks on a slow response.
   */
  readonly refresh: () => Promise<void>;
  readonly dropTail: () => void;
  readonly discardStale: () => void;
}

export function useCursorFeed<P extends CursorPage>(
  opts: CursorFeedOptions<P>,
): CursorFeed<P> {
  const { identity, enabled = true } = opts;
  // Callbacks are read through a ref so the stable `loadMore` / `refresh`
  // always see the consumer's CURRENT filters and never a closure from the
  // render that created them.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const [state, setState] = useState<CursorFeedState<P>>(() =>
    initialFeed(opts.seed?.(identity) ?? null),
  );
  // Mirror for the imperative callbacks (they must read the latest state
  // without being re-created on every render).
  const stateRef = useRef(state);
  stateRef.current = state;
  const genRef = useRef(0);

  const landFirstPage = useCallback((gen: number, page: P): void => {
    setState((s) => landFirst(s, gen, page, optsRef.current.isSamePage));
    if (genRef.current === gen) optsRef.current.onFirstPage?.(page);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    genRef.current += 1;
    const gen = genRef.current;
    setState((s) => resetFeed(s, gen, optsRef.current.seed?.(identity) ?? null));
    optsRef.current.fetchPage(null).then(
      (page) => landFirstPage(gen, page),
      (e: unknown) => setState((s) => failFirst(s, gen, errorMessage(e))),
    );
    // Supersession / unmount: bump so a late response knows it is stale.
    return () => {
      genRef.current += 1;
    };
  }, [identity, enabled, landFirstPage]);

  const loadMore = useCallback((): void => {
    const s = stateRef.current;
    if (s.loadingMore || s.loadingFirst || s.cursor === null) return;
    const from = s.cursor;
    const gen = genRef.current;
    // Mark synchronously too: two sentinel callbacks before the next render
    // must not issue two requests for the same cursor.
    stateRef.current = startMore(s);
    setState(startMore);
    optsRef.current.fetchPage(from).then(
      (page) => setState((x) => landMore(x, gen, from, page)),
      (e: unknown) => setState((x) => failMore(x, gen, errorMessage(e))),
    );
  }, []);

  const refresh = useCallback((): Promise<void> => {
    // A disabled feed (the browser on a non-table view) has nothing to
    // re-read; its consumer's refresh triggers keep firing regardless.
    if (optsRef.current.enabled === false || stateRef.current.loadingFirst) {
      return Promise.resolve();
    }
    const gen = genRef.current;
    return optsRef.current.fetchPage(null).then(
      (page) => landFirstPage(gen, page),
      (e: unknown) => {
        if (optsRef.current.surfaceRefreshFailures === true) {
          setState((s) => failFirst(s, gen, errorMessage(e)));
        }
      },
    );
  }, [landFirstPage]);

  const dropTailNow = useCallback((): void => setState(dropTail), []);
  const discardStaleNow = useCallback((): void => setState(discardStale), []);

  const { rows, tailRows } = useMemo(
    () => feedRows(state, optsRef.current.rowKey),
    // The key function is a pure projection of the row; only the pages matter.
    [state.first, state.tail], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return useMemo(
    () => ({
      first: state.first === null ? null : state.first.page,
      firstIsLive: firstIsLive(state),
      loadingFirst: state.loadingFirst,
      rows,
      tailRows,
      hasMore: state.cursor !== null,
      loadingMore: state.loadingMore,
      error: state.error,
      loadMore,
      refresh,
      dropTail: dropTailNow,
      discardStale: discardStaleNow,
    }),
    [state, rows, tailRows, loadMore, refresh, dropTailNow, discardStaleNow],
  );
}
